import pluralize from 'pluralize'
import {
  Children,
  type JSX,
  ReactNode,
  cloneElement,
  isValidElement,
  useMemo,
} from 'react'
import { z } from 'zod'

import { filterCollection } from '@hap/analytics'
import { Button, Icon, Popover, PopoverContent, PopoverTrigger } from '@hap/components'

import { useDatastore } from '../datastore'
import { useFilterAccess } from '../filter-control/use-filter-access'

export function CommandPopover(props: Props): JSX.Element | null {
  const { data } = useDatastore(props.collection)
  const { isFilterAllowed } = useFilterAccess()
  const filterAllowed = !props.filterField || isFilterAllowed(props.filterField)

  const labels = useMemo(
    () => data?.filter((record) => props.selected?.includes(record.id)) || [],
    [data, props.selected],
  )

  if (!filterAllowed && !props.selected?.length) return null

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          disabled={props.disabled || !filterAllowed}
          variant={props.selected && props.selected.length ? 'default' : 'secondary'}
          className="pr-1"
        >
          {renderLabel()}
          <Icon
            name="triangle"
            weight="fill"
            size={10}
            className="mr-2 ml-1 rotate-180"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-0">
        {Children.map(props.children, (child) =>
          isValidElement<{ selected?: string[] }>(child)
            ? cloneElement(child, { selected: props.selected })
            : child,
        )}
      </PopoverContent>
    </Popover>
  )

  function renderLabel() {
    const length = labels.length
    switch (length) {
      case 0:
        return props.label
      case 1:
        return labels[0].name
      default:
        return `${length} ${pluralize(props.label, length)}`
    }
  }
}

interface Props {
  selected?: string[]
  children: ReactNode
  label: string
  collection: z.infer<typeof filterCollection>
  disabled?: boolean
  filterField?: string
}
